/**
 * PATH: packages/email-hub/src/feature/lib/composeResume.ts
 * PURPOSE: Pure decisions for the cross-device compose draft resume, kept
 *          out of the big useComposePage hook so they can be unit-tested
 *          in isolation. Two calls:
 *            - shouldHydrateServerBody: when does the SERVER copy win over
 *              the locally-resumed body?
 *            - attachmentsToAdd: which downloaded server attachments may be
 *              merged back into the live set?
 * WHY:     These two decisions are where the resume historically corrupted
 *          drafts — see BUG-2026-06-25-COMPOSE-DELETED-IMAGE-RESURRECTS and
 *          BUG-2026-06-25-COMPOSE-SERVER-RESUME-READDS-ATTACHMENT.
 */

/**
 * Should the cross-device server draft body overwrite the locally-resumed
 * body? RECENCY ONLY — the caller must have already cleared the case where
 * the operator edited since the local resume (that edit guard lives in the
 * hook because it needs the live composer snapshot).
 *
 * We deliberately do NOT prefer the server copy because it has more inline
 * images: that exact heuristic resurrected an image the operator had just
 * deleted (the server still carried it until its own delete synced). Recency
 * is correct for BOTH real cases:
 *   (a) operator deletes an image → the post-delete local autosave is the
 *       freshest, so `localTime > serverTime` and local wins (image stays
 *       gone);
 *   (b) localStorage dropped the body on a quota overflow → the failed write
 *       left `localTime` stale, so `serverTime >= localTime` and the server
 *       copy is pulled back in.
 */
export function shouldHydrateServerBody(
  hasServerContent: boolean,
  serverTime: number,
  localTime: number,
): boolean {
  return hasServerContent && serverTime >= localTime
}

/**
 * Of the attachments downloaded from the server draft, which may be merged
 * into the live attachment set? Skip any that are:
 *   - already present (`presentKeys`) — never double a file, and
 *   - on the operator's removed list (`removedKeys`) — the one-shot resume
 *     must never resurrect a file the operator deleted while the download
 *     round-trip was still in flight (BUG #10).
 * Keys are `name:size`.
 */
export function attachmentsToAdd<T extends { name: string; size: number }>(
  downloaded: T[],
  presentKeys: Set<string>,
  removedKeys: Set<string>,
): T[] {
  return downloaded.filter((f) => {
    const key = `${f.name}:${f.size}`
    return !presentKeys.has(key) && !removedKeys.has(key)
  })
}
